import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SCHOOL_NAMES from '../schoolNames';

const API = process.env.REACT_APP_API_URL || '';

export default function SchoolSelect() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(user?.school || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); return; }
    const q = query.toLowerCase();
    const matches = SCHOOL_NAMES.filter(n => n.toLowerCase().includes(q)).slice(0, 8);
    setSuggestions(matches);
    setOpen(matches.length > 0);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target) && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (name) => {
    setSelected(name);
    setQuery(name);
    setOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/set-school`, { school: selected || null });
      await refreshUser();
      navigate('/', { replace: true });
    } catch {
      navigate('/', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => navigate('/', { replace: true });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cb-bg)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Static navbar (non-interactive) ── */}
      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <nav className="navbar lp-topnav" style={{ position: 'relative', zIndex: 10 }}>
          <div className="navbar-brand">
            <img src="/RANKEDSATLOGO.png" alt="RankedSAT" style={{ height: 'auto', width: 160, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <span className="navbar-link">Leaderboard</span>
            <span className="navbar-link">About</span>
          </div>
          <div className="navbar-right">
            <span className="btn btn-ghost btn-sm" style={{ fontWeight: 600, color: 'rgba(255,255,255,.85)' }}>Help</span>
          </div>
        </nav>
      </div>

      <div className="login-page" style={{ flex: 1 }}>
      <div className="login-card school-select-card">
        <div className="school-select-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 32, height: 32 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>

        <h2 className="school-select-title">What school do you go to?</h2>
        <p className="school-select-subtitle">
          Add your school to show a flair next to your name in matchmaking and on the leaderboard.
        </p>

        <div className="school-search-wrap" style={{ position: 'relative' }}>
          <div className="school-search-input-wrap">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, color: 'var(--cb-gray-400)' }}>
              <circle cx="9" cy="9" r="6"/><path d="m15 15-3-3"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="form-input school-search-input"
              placeholder="Search for your school…"
              value={query}
              onChange={e => { setQuery(e.target.value); if (selected && e.target.value !== selected) setSelected(''); }}
              onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
              autoFocus
            />
            {query && (
              <button type="button" className="school-clear-btn" onClick={() => { setQuery(''); setSelected(''); setSuggestions([]); setOpen(false); inputRef.current?.focus(); }}>
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
                  <path d="M15 5L5 15M5 5l10 10"/>
                </svg>
              </button>
            )}
          </div>

          {open && (
            <div className="school-dropdown" ref={dropRef}>
              {suggestions.map((s, i) => (
                <button key={i} type="button" className={`school-option${selected === s ? ' selected' : ''}`} onMouseDown={() => pick(s)}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.5 }}>
                    <path d="M2 8l8-6 8 6v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/>
                    <polyline points="8 19 8 13 12 13 12 19"/>
                  </svg>
                  <span>{s}</span>
                  {selected === s && (
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, marginLeft: 'auto', color: 'var(--cb-blue)' }}>
                      <path d="M4 10l4 4 8-8"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div className="school-preview">
            <span className="school-flair">{selected}</span>
            <span style={{ fontSize: 13, color: 'var(--cb-gray-500)', marginLeft: 8 }}>This will show next to your name</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={handleSkip}>
            Skip for now
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 2 }}
            onClick={handleSave}
            disabled={saving || !selected}
          >
            {saving ? 'Saving…' : 'Save School'}
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--cb-gray-400)', marginTop: 12, textAlign: 'center' }}>
          You can always change this later in your profile settings.
        </p>
      </div>
      </div>
    </div>
  );
}
